/**
 * Checkout id — the analytics key that joins the client-side checkout funnel
 * to the server-side order events.
 *
 * Stability rule (PostHog schema v2.1, section 4.5): the id is minted on the
 * FIRST entry to checkout and reused on every subsequent checkout_* event
 * until that basket is submitted or emptied. An engineer who visits checkout,
 * goes back to change the basket, and returns keeps the SAME checkout_id, so
 * the funnel joins every visit and the eventual order to one basket.
 *
 * checkout_started itself fires once per basket, not once per visit: re-entering
 * an UNCHANGED basket is a revisit and emits nothing, while adding, removing, or
 * re-quantifying a line makes it a new checkout and emits again (still under the
 * same id). shouldFireCheckoutStarted() enforces this by comparing a per-scope
 * contents signature; its lifecycle matches the id's exactly.
 *
 * Scoped per branch checkout, not per basket. The cart opens a SEPARATE
 * checkout flow per branch group ("Checkout This Branch"), so a single
 * basket-wide id would let branch B's order_placed join branch A's
 * checkout_started — making an abandoned branch A look converted. Each
 * branch/fulfilment pairing therefore gets its own id, and re-entering the
 * same group still reuses it, which is what the stability rule is protecting.
 *
 * Deliberately NOT the Cart UUID: Cart is one permanent row per user per
 * tenant, so checkout empties the items but the row and its id live on — every
 * checkout an engineer ever made would share a single id.
 *
 * localStorage, not sessionStorage: the id belongs to the BASKET, and the
 * basket is server-side, so it outlives the tab. Engineers work on mobile,
 * where browsers evict background tabs aggressively — with sessionStorage
 * every eviction would mint a fresh id and report a phantom abandonment for
 * the basket the engineer then comes back and orders. The ids are cleared on
 * submit, on the basket being emptied, and on logout, so none outlives the
 * basket it belongs to.
 */

const CHECKOUT_ID_STORAGE_KEY = 'ss.checkout_ids'
const CHECKOUT_SIG_STORAGE_KEY = 'ss.checkout_started_sigs'
const CHECKOUT_USER_STORAGE_KEY = 'ss.checkout_ids_user'
const LOGIN_REPORTED_USER_KEY = 'ss.login_reported_user'

/**
 * Read the identity provider's session id from the stored access token.
 *
 * Used purely as a de-duplication key for the login event — never for an
 * access decision — so the token is decoded, not verified. The backend
 * verifies it properly on every request.
 *
 * @returns The `sid` claim, or null when there is no readable token.
 */
function currentAuthSessionId(): string | null {
  try {
    const token = localStorage.getItem('access_token')
    if (!token) return null
    const [, payload] = token.split('.')
    if (!payload) return null
    const claims: unknown = JSON.parse(
      atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
    )
    if (typeof claims !== 'object' || claims === null) return null
    const sid = (claims as Record<string, unknown>).sid
    return typeof sid === 'string' && sid ? sid : null
  } catch {
    // Malformed or unreadable token — fall back to the user-only key below.
    return null
  }
}

/**
 * Whether user_logged_in still needs reporting for this authenticated session.
 *
 * Keyed on the identity provider's session id, not the browser tab, because
 * the app shares its tokens across tabs via localStorage — one WorkOS session
 * is one authenticated session however many tabs it is open in. That makes a
 * second tab, and closing and reopening one, correctly silent, while a real
 * sign-in reports because re-authenticating mints a new session id.
 *
 * The user is included in the key so a different engineer on a shared device
 * always reports, even in the unlikely event of a session id collision.
 *
 * Falls back to the user alone when no session id is readable, which keeps
 * the guard working rather than reporting on every page load.
 *
 * @param userId The currently identified user's UUID.
 * @returns True when this authenticated session has not reported a login yet.
 */
export function shouldReportLogin(userId: string): boolean {
  const key = `${userId}:${currentAuthSessionId() ?? 'no-session'}`
  try {
    if (localStorage.getItem(LOGIN_REPORTED_USER_KEY) === key) {
      return false
    }
    localStorage.setItem(LOGIN_REPORTED_USER_KEY, key)
    return true
  } catch {
    // Storage unavailable (private browsing): report rather than stay silent.
    // Over-reporting a login is recoverable; never reporting one is not.
    return true
  }
}

/** Forget the reported login, so the next sign-in reports again. */
export function clearReportedLogin(): void {
  try {
    localStorage.removeItem(LOGIN_REPORTED_USER_KEY)
  } catch {
    // Nothing stored means nothing to clear.
  }
}

type CheckoutIdMap = Record<string, string>

/**
 * Build the storage scope for one branch checkout.
 *
 * Keyed on fulfilment mode as well as branch: the same branch can appear as
 * both a collection and a delivery group, and those are separate checkouts.
 *
 * @param fulfillmentMode Collection or delivery, as the cart opened it.
 * @param branchId The branch group being checked out, or null when absent.
 * @returns A stable scope key for this checkout flow.
 */
export function checkoutScope(
  fulfillmentMode: string,
  branchId: string | null,
  isBackorder: boolean = false,
): string {
  // DEV-51018: the same supplier+branch can produce two sibling checkouts
  // (in-stock and backorder). They must NOT share a checkout_id — a
  // shared id would let one's checkout_started join the other's
  // checkout_abandoned and misreport conversion. Non-backorder scopes
  // stay unchanged so today's in-stock funnel numbers are not perturbed.
  const base = `${fulfillmentMode}:${branchId ?? 'default'}`
  return isBackorder ? `${base}${BACKORDER_SCOPE_SUFFIX}` : base
}

// DEV-51018: single source of truth for the backorder suffix. The pruner
// and any future caller decompose scopes via parseCheckoutScope so the
// two sides can never disagree on where the branchId ends.
const BACKORDER_SCOPE_SUFFIX = ':backorder'

/**
 * Inverse of checkoutScope — split a stored scope string back into its parts.
 *
 * The scope format is `${mode}:${branchId}` optionally followed by
 * `:backorder`. The branchId itself may contain colons in theory (it doesn't
 * today, but nothing prevents it), so we peel the suffix off the end rather
 * than splitting greedily. Returns null when the scope has no mode / branch
 * pair to speak of (defensive — pruning should skip unrecognised scopes
 * rather than treating the whole string as a branchId).
 */
export function parseCheckoutScope(
  scope: string,
): { mode: string; branchId: string; isBackorder: boolean } | null {
  const separatorIndex = scope.indexOf(':')
  if (separatorIndex < 0) return null
  const mode = scope.slice(0, separatorIndex)
  let branchId = scope.slice(separatorIndex + 1)
  const isBackorder = branchId.endsWith(BACKORDER_SCOPE_SUFFIX)
  if (isBackorder) {
    branchId = branchId.slice(0, -BACKORDER_SCOPE_SUFFIX.length)
  }
  return { mode, branchId, isBackorder }
}

/**
 * Read the checkout id for this scope, minting one if this is the first entry
 * to checkout for it.
 *
 * @param scope Key from checkoutScope().
 * @returns A UUID string, stable across re-navigations to the same checkout.
 */
export function getOrMintCheckoutId(scope: string): string {
  const stored = readCheckoutIds()
  const existing = stored[scope]
  if (existing) {
    return existing
  }

  const minted = mintUuid()
  writeCheckoutIds({ ...stored, [scope]: minted })
  return minted
}

/**
 * Decide whether checkout_started should fire for this checkout, deduping by
 * basket contents so it fires once per basket rather than once per visit.
 *
 * The first entry for a scope has no stored signature and fires. A later entry
 * whose contents signature matches the last one that fired is an unchanged
 * revisit and stays silent. Any add, removal, or quantity change moves the
 * signature and fires again — under the same checkout_id, since the basket is
 * still the same funnel.
 *
 * Records the new signature only when it decides to fire, so the stored value
 * always reflects the contents of the last emitted event. When storage is
 * unavailable it returns true (fires) rather than swallow a real checkout —
 * an extra event is recoverable, a missing one is not.
 *
 * @param scope Key from checkoutScope().
 * @param contentsSignature Stable digest of the group's lines and quantities.
 * @returns True when checkout_started should be emitted for this entry.
 */
export function shouldFireCheckoutStarted(
  scope: string,
  contentsSignature: string,
): boolean {
  const stored = readScopedMap(CHECKOUT_SIG_STORAGE_KEY)
  if (stored[scope] === contentsSignature) {
    return false
  }
  writeScopedMap(CHECKOUT_SIG_STORAGE_KEY, {
    ...stored,
    [scope]: contentsSignature,
  })
  return true
}

/**
 * Generate a v4 UUID, without assuming a secure context.
 *
 * crypto.randomUUID() only exists on HTTPS and localhost, so it is missing
 * when the dev server is reached over a LAN IP — which is exactly how this
 * mobile-first app gets tested on a phone. Calling it blindly would throw
 * inside checkout. Falls back to getRandomValues, then to Math.random: the
 * id only has to be unique enough to join two events, and an analytics id
 * must never be able to break a checkout.
 */
export function mintUuid(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }

  const bytes = new Uint8Array(16)
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    crypto.getRandomValues(bytes)
  } else {
    for (let index = 0; index < bytes.length; index++) {
      bytes[index] = Math.floor(Math.random() * 256)
    }
  }

  // Set the version (4) and variant bits so the value parses as a real UUID —
  // the backend stores null for anything uuid.UUID() rejects.
  bytes[6] = (bytes[6] & 0x0f) | 0x40
  bytes[8] = (bytes[8] & 0x3f) | 0x80

  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20),
  ].join('-')
}

/**
 * Clear the checkout id for one scope. Call after a successful submit, so the
 * next checkout of that branch starts a new funnel while any other branch's
 * in-progress checkout keeps its own id.
 *
 * @param scope Key from checkoutScope().
 */
export function clearCheckoutId(scope: string): void {
  removeScope(CHECKOUT_ID_STORAGE_KEY, scope)
  // The next checkout of this branch is a fresh basket, so its first entry must
  // fire checkout_started again — drop the contents signature alongside the id.
  removeScope(CHECKOUT_SIG_STORAGE_KEY, scope)
}

function removeScope(storageKey: string, scope: string): void {
  const stored = readScopedMap(storageKey)
  if (!(scope in stored)) {
    return
  }
  const remaining = { ...stored }
  delete remaining[scope]
  writeScopedMap(storageKey, remaining)
}

/**
 * Drop stored checkout ids (and their signatures) whose group is gone.
 *
 * A branch group can empty while the rest of the basket lives on — removing
 * the last line for branch A while branch B still has items. That group's
 * checkout is over, so its id must not be reused: re-adding an item for it
 * later would otherwise join the new order to the abandoned checkout's funnel
 * and score it converted.
 *
 * The id and signature are pruned together (they share one lifecycle), by two
 * different tests keyed to how each scope is stored:
 *
 * - PICKUP scopes are keyed by the item's own branch, which liveBranchIds
 *   holds — so a pickup scope dies when its branch leaves the basket.
 * - DELIVERY scopes are keyed by the DELIVERING branch, a borrowed source when
 *   the adapter cross-borrows, which liveBranchIds cannot see (useCart only
 *   knows each line's own branch). Judging them by branch would wrongly drop a
 *   still-live borrowed scope on every cart visit and re-fire checkout_started.
 *   So a delivery scope is judged by its CONTENTS instead: it stays live while
 *   any item its signature covers is still in the basket, and is dropped once
 *   its whole group is gone. A delivery scope with no stored signature cannot
 *   be judged, so it is kept (dropping a live scope is the worse error).
 *
 * The no-branch 'default' fallback is always kept; clearAllCheckoutIds covers
 * it when the basket empties.
 *
 * @param liveBranchIds Own-branch ids currently represented in the basket.
 * @param liveItemKeys Per-line keys (id, else `productId:branchId`) currently
 *   in the basket, matching the signature's own line-key derivation.
 */
export function pruneCheckoutIds(
  liveBranchIds: ReadonlySet<string>,
  liveItemKeys: ReadonlySet<string>,
): void {
  const ids = readScopedMap(CHECKOUT_ID_STORAGE_KEY)
  const signatures = readScopedMap(CHECKOUT_SIG_STORAGE_KEY)

  const scopesToDrop = new Set<string>()
  for (const scope of new Set([
    ...Object.keys(ids),
    ...Object.keys(signatures),
  ])) {
    // DEV-51018: use the shared parser so a `:backorder` suffix on
    // sibling checkouts doesn't get baked into `branchId` — the pruner
    // would otherwise fail every `liveBranchIds.has(...)` check for
    // backorder scopes and nuke live sessions on every cart visit.
    const parsed = parseCheckoutScope(scope)
    if (!parsed) continue
    const { mode, branchId } = parsed
    if (branchId === 'default') {
      continue
    }
    if (mode === 'delivery') {
      const signature = signatures[scope]
      if (signature && !signatureHasLiveItem(signature, liveItemKeys)) {
        scopesToDrop.add(scope)
      }
    } else if (!liveBranchIds.has(branchId)) {
      scopesToDrop.add(scope)
    }
  }

  if (scopesToDrop.size === 0) {
    return
  }
  writeScopedMap(CHECKOUT_ID_STORAGE_KEY, omitScopes(ids, scopesToDrop))
  writeScopedMap(CHECKOUT_SIG_STORAGE_KEY, omitScopes(signatures, scopesToDrop))
}

/**
 * Whether a stored contents signature still covers a line in the basket.
 *
 * Each signature entry is `<line-key>:<quantity>`; the line key itself may
 * contain a colon (the `productId:branchId` fallback), so the quantity is split
 * off at the LAST colon and the remainder compared against the live keys.
 */
function signatureHasLiveItem(
  signature: string,
  liveItemKeys: ReadonlySet<string>,
): boolean {
  return signature.split('|').some((entry) => {
    const lineKey = entry.slice(0, entry.lastIndexOf(':'))
    return lineKey.length > 0 && liveItemKeys.has(lineKey)
  })
}

function omitScopes(
  map: CheckoutIdMap,
  scopesToDrop: ReadonlySet<string>,
): CheckoutIdMap {
  const remaining: CheckoutIdMap = {}
  for (const [scope, value] of Object.entries(map)) {
    if (!scopesToDrop.has(scope)) {
      remaining[scope] = value
    }
  }
  return remaining
}

/**
 * Drop stored ids when the signed-in user changes.
 *
 * Baskets are per-user, so one user's checkout ids must never be reused by
 * another. Logout already clears them, but engineers share devices and a
 * session can end without passing through the logout page (browser closed,
 * token expiry handled elsewhere). Keyed on the identified user rather than
 * on a "logged out" signal, so it cannot fire spuriously while the user
 * record is still loading.
 *
 * The last user is held in localStorage, so this comparison survives the
 * full-page redirect a logout performs — unlike an in-memory ref. Callers use
 * the return value to reset other per-user analytics state (e.g. PostHog) on
 * the same signal.
 *
 * @param userId The currently identified user's UUID.
 * @returns True when a DIFFERENT real user was signed in on this device before
 *   (an A → B switch); false on the first identification or the same user.
 */
export function clearCheckoutIdsOnUserChange(userId: string): boolean {
  try {
    const previous = localStorage.getItem(CHECKOUT_USER_STORAGE_KEY)
    if (previous === userId) {
      return false
    }
    localStorage.setItem(CHECKOUT_USER_STORAGE_KEY, userId)
    if (previous !== null) {
      clearAllCheckoutIds()
      return true
    }
    return false
  } catch {
    // Storage unavailable — nothing persisted, so nothing to reconcile.
    return false
  }
}

/**
 * Forget which user last held checkout ids on this device.
 *
 * Call only from the handled logout, which has already cleared the checkout ids
 * and reset PostHog. Clearing the marker too means the next sign-in reads as a
 * first identification, not a user switch — so clearCheckoutIdsOnUserChange
 * returns false and App.tsx does not fire its PostHog reset a SECOND time,
 * which would discard the anonymous session the new sign-in accumulated during
 * login/callback and orphan those events. The backstop still works when logout
 * was SKIPPED (browser closed): this never ran, so the marker still names the
 * previous user and the switch is detected.
 */
export function clearCheckoutUser(): void {
  try {
    localStorage.removeItem(CHECKOUT_USER_STORAGE_KEY)
  } catch {
    // Nothing stored means nothing to clear.
  }
}

/**
 * Drop every stored checkout id. Call when the basket is emptied (all its
 * checkouts are void) or on logout (the next user must not inherit them).
 */
export function clearAllCheckoutIds(): void {
  try {
    localStorage.removeItem(CHECKOUT_ID_STORAGE_KEY)
    // Signatures share the ids' lifecycle — an emptied basket or a new user
    // must start every checkout_started fresh.
    localStorage.removeItem(CHECKOUT_SIG_STORAGE_KEY)
  } catch {
    // Nothing stored means nothing to clear.
  }
}

function readScopedMap(storageKey: string): CheckoutIdMap {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) {
      return {}
    }
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return {}
    }
    // Drop any non-string values rather than trusting whatever is on disk —
    // a corrupted entry must not propagate into an event property.
    return Object.fromEntries(
      Object.entries(parsed as Record<string, unknown>).filter(
        (entry): entry is [string, string] => typeof entry[1] === 'string',
      ),
    )
  } catch {
    // Unparseable or storage-disabled: behave as if nothing were stored.
    return {}
  }
}

function writeScopedMap(storageKey: string, map: CheckoutIdMap): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify(map))
  } catch {
    // Private browsing / storage full: the caller still gets a usable id for
    // this navigation. The funnel loses the re-entry join rather than the order.
  }
}

function readCheckoutIds(): CheckoutIdMap {
  return readScopedMap(CHECKOUT_ID_STORAGE_KEY)
}

function writeCheckoutIds(ids: CheckoutIdMap): void {
  writeScopedMap(CHECKOUT_ID_STORAGE_KEY, ids)
}
